import { getMomentTz } from "@dexteel/mesf-core";
import {
	Box,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import type { EChartsOption } from "echarts";
import ReactECharts from "echarts-for-react";
import { useEffect, useRef } from "react";
import { renderTooltipDot } from "../../../shared/consumption/components/consumption-chart/PeriodBarsChart";
import type { UtilityConsumptionMiniBucket } from "../../../shared/consumption/models/UtilityConsumption";
import {
	formatMeasureWithUnit,
	VOLUME_UNIT,
} from "../../../shared/consumption/utils/format";
import { CONSUMPTION_LABEL_SX } from "../../../shared/consumption/utils/tokens";

const moment = getMomentTz();
const CURRENT_SERIES_COLOR = "#1565C0";
const PREVIOUS_SERIES_COLOR = "#8CC0EC";
const REFERENCE_COLOR = "#0A3D6B";
const CHART_HEIGHT = 190;

const SERIES_NAME = {
	CURRENT: "Last 7 days",
	PREVIOUS: "Previous 7 days",
	AVERAGE: "30-day daily average",
} as const;

const getTooltipColors = (parameters: unknown): Map<string, string> => {
	if (!Array.isArray(parameters)) return new Map();
	return new Map(
		parameters.flatMap((entry): [string, string][] => {
			if (typeof entry !== "object" || entry === null) return [];
			const record = entry as Record<string, unknown>;
			return typeof record.seriesName === "string" &&
				typeof record.color === "string"
				? [[record.seriesName, record.color]]
				: [];
		}),
	);
};

const renderTooltipDash = (color: string | undefined) =>
	`<span style="display:inline-block;width:12px;border-top:2px dashed ${
		color ?? "transparent"
	};margin-right:6px;vertical-align:middle"></span>`;

const getTooltipIndex = (parameters: unknown): number | null => {
	if (!Array.isArray(parameters) || parameters.length === 0) return null;
	const first = parameters[0];
	if (typeof first !== "object" || first === null || !("dataIndex" in first)) {
		return null;
	}
	const dataIndex = first.dataIndex;
	return typeof dataIndex === "number" ? dataIndex : null;
};

interface PairedDaysChartProps {
	buckets: UtilityConsumptionMiniBucket[];
	averageGallons: number | null;
	unit?: string;
	measureName?: string;
	subject?: string;
}

export const PairedDaysChart = ({
	buckets,
	averageGallons,
	unit = VOLUME_UNIT,
	measureName = "Gallons",
	subject = "water",
}: PairedDaysChartProps) => {
	const withUnit = (value: number | null) => formatMeasureWithUnit(value, unit);
	const chartContainerRef = useRef<HTMLDivElement>(null);
	const chartRef = useRef<ReactECharts>(null);

	useEffect(() => {
		const container = chartContainerRef.current;
		if (!container) return;

		const resizeObserver = new ResizeObserver(() => {
			chartRef.current?.getEchartsInstance().resize();
		});
		resizeObserver.observe(container);

		return () => resizeObserver.disconnect();
	}, []);

	const labels = buckets.map((bucket) =>
		moment(bucket.CurrentBucketStartUtc).format("ddd M/D"),
	);
	const options: EChartsOption = {
		animationDuration: 300,
		color: [CURRENT_SERIES_COLOR, PREVIOUS_SERIES_COLOR, REFERENCE_COLOR],
		tooltip: {
			trigger: "axis",
			axisPointer: { type: "shadow" },
			confine: true,
			formatter: (parameters: unknown) => {
				const index = getTooltipIndex(parameters);
				if (index === null) return "Consumption unavailable";
				const bucket = buckets[index];
				if (!bucket) return "Consumption unavailable";
				const colors = getTooltipColors(parameters);
				return [
					`<strong>${labels[index]}</strong>`,
					`${renderTooltipDot(colors.get(SERIES_NAME.CURRENT))}${SERIES_NAME.CURRENT} · ${moment.utc(bucket.CurrentBucketStartUtc).format("MMM D, YYYY")}: ${withUnit(bucket.CurrentGallons)}`,
					`${renderTooltipDot(colors.get(SERIES_NAME.PREVIOUS))}${SERIES_NAME.PREVIOUS} · ${moment.utc(bucket.PreviousBucketStartUtc).format("MMM D, YYYY")}: ${withUnit(bucket.PreviousGallons)}`,
					`${renderTooltipDash(colors.get(SERIES_NAME.AVERAGE))}${SERIES_NAME.AVERAGE}: ${withUnit(averageGallons)}`,
				].join("<br/>");
			},
		},
		legend: {
			type: "plain",
			top: 0,
			left: "center",
			width: "96%",
			itemWidth: 14,
			itemHeight: 8,
			textStyle: { fontSize: 11 },
		},
		grid: {
			left: 8,
			right: 8,
			top: 48,
			bottom: 8,
			containLabel: true,
		},
		xAxis: {
			type: "category",
			data: labels,
			axisLabel: { fontSize: 10, interval: 0 },
		},
		yAxis: {
			type: "value",
			name: measureName,
			nameGap: 10,
			axisLabel: { fontSize: 10 },
		},
		series: [
			{
				name: SERIES_NAME.CURRENT,
				type: "bar",
				data: buckets.map((bucket) => bucket.CurrentGallons),
				barMaxWidth: 18,
				itemStyle: { color: CURRENT_SERIES_COLOR },
			},
			{
				name: SERIES_NAME.PREVIOUS,
				type: "bar",
				data: buckets.map((bucket) => bucket.PreviousGallons),
				barMaxWidth: 18,
				itemStyle: { color: PREVIOUS_SERIES_COLOR },
			},
			{
				name: SERIES_NAME.AVERAGE,
				type: "line",
				symbol: "none",
				data: buckets.map(() => averageGallons),
				lineStyle: {
					color: REFERENCE_COLOR,
					type: "dashed",
					width: 2,
				},
			},
		],
	};

	return (
		<Paper
			variant="outlined"
			sx={{
				gridArea: "chart",
				position: "relative",
				minWidth: 0,
				maxWidth: "100%",
				p: 0.75,
			}}
		>
			<Typography
				component="div"
				sx={{ ...CONSUMPTION_LABEL_SX, px: 0.5, pb: 0.25 }}
			>
				Last 7 vs previous 7 days
			</Typography>
			<Box ref={chartContainerRef} sx={{ width: "100%", minWidth: 0 }}>
				<ReactECharts
					ref={chartRef}
					option={options}
					notMerge
					opts={{ renderer: "svg" }}
					style={{ height: CHART_HEIGHT, width: "100%" }}
					role="img"
					aria-label={`Last 7 and Previous 7 complete-day ${subject} consumption comparison chart`}
				/>
			</Box>
			<Box
				sx={{
					position: "absolute",
					top: 0,
					left: 0,
					width: 1,
					height: 1,
					m: -1,
					p: 0,
					overflow: "hidden",
					clip: "rect(0 0 0 0)",
					clipPath: "inset(50%)",
					whiteSpace: "nowrap",
					border: 0,
				}}
			>
				<Table
					size="small"
					aria-label="At a Glance paired consumption chart values"
				>
					<caption>
						Last 7 and Previous 7 complete plant-local days with the 30-day
						daily average
					</caption>
					<TableHead>
						<TableRow>
							<TableCell component="th" scope="col">
								Day
							</TableCell>
							<TableCell component="th" scope="col">
								Last 7 date
							</TableCell>
							<TableCell component="th" scope="col">
								Last 7 ({unit})
							</TableCell>
							<TableCell component="th" scope="col">
								Previous 7 date
							</TableCell>
							<TableCell component="th" scope="col">
								Previous 7 ({unit})
							</TableCell>
							<TableCell component="th" scope="col">
								30-day daily average
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{buckets.map((bucket, index) => (
							<TableRow key={bucket.CurrentBucketStartUtc?.toISOString()}>
								<TableCell component="th" scope="row">
									{labels[index]}
								</TableCell>
								<TableCell>
									{moment
										.utc(bucket.CurrentBucketStartUtc)
										.format("MMM D, YYYY")}
								</TableCell>
								<TableCell>{withUnit(bucket.CurrentGallons)}</TableCell>
								<TableCell>
									{moment
										.utc(bucket.PreviousBucketStartUtc)
										.format("MMM D, YYYY")}
								</TableCell>
								<TableCell>{withUnit(bucket.PreviousGallons)}</TableCell>
								<TableCell>{withUnit(averageGallons)}</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</Box>
		</Paper>
	);
};
